import { formatMetricDisplayScore, getDisplayValue, getProgressPercentage } from "@Components/common/byb-ds/cards/byb-metric-card.utils";
import { AppContext } from "@Context/common/general/context";
import MetricFilterTypes from "@Enums/MetricFilterTypes";
import { getPerformanceShade } from "@Helpers/feedback";
import useI18n from "@i18n";
import { Box, LinearProgress, Stack, Typography, useTheme } from "@mui/material";
import FillingBrainIcon from "@Resources/app/icons-v2/system-icons/filling-brain-icon";
import { useContext } from "react";

interface BootcampMetricResultProps {
  metric: MetricFilterTypes;
  score?: number;
  target?: number;
}

const BootcampMetricResult = ({ metric, score, target }: BootcampMetricResultProps) => {
  const theme = useTheme();
  const { i18n } = useI18n();
  const { state } = useContext(AppContext);
  const displayValue = getDisplayValue(metric, score);
  const formattedValue = formatMetricDisplayScore(metric, displayValue, 0);
  const metricName = i18n(`metric.${metric.toLowerCase()}.name`);

  if (metric === MetricFilterTypes.BYB_IQ) {
    const fillPercentage = Math.max(0, Math.min((score ?? 0) / 10, 100));
    const brainTarget = target !== undefined && target > 0 ? Math.max(0, Math.min(target / 10, 100)) : undefined;

    return (
      <Stack alignItems="center" gap={2} aria-label={metricName}>
        <FillingBrainIcon size={160} fillPercentage={fillPercentage} target={brainTarget} showShadow={false} />
        <Stack alignItems="center" gap={0.5}>
          <Typography variant="text5xl700" color="dsBase.v200">
            {formattedValue ?? "—"}
          </Typography>
          <Typography variant="text1xl400" color="dsNeutral.v300">
            {metricName}
          </Typography>
        </Stack>
      </Stack>
    );
  }

  const [metricProgress = 0, targetProgress] = [score, target].map(value => getProgressPercentage(metric, value));
  const creationDate = state.user.creationDate ? new Date(state.user.creationDate).getTime() : null;
  const performanceColor = displayValue === null ? null : getPerformanceShade(metric, displayValue, theme, creationDate);
  const progressGradient = performanceColor
    ? `linear-gradient(to right, ${performanceColor.v100} 0%, ${performanceColor.v200} 75%, ${performanceColor.v300} 100%)`
    : theme.palette.dsPrimaryGradient.green;

  return (
    <Stack gap={2}>
      <Stack direction="row" alignItems="flex-end" justifyContent="space-between" gap={2}>
        <Stack direction="row" alignItems="baseline" gap={0.75}>
          <Typography variant="text5xl700" color="dsBase.v200">
            {formattedValue ?? "—"}
          </Typography>
          {formattedValue !== null && (
            <Typography variant="text1xl500" color="dsNeutral.v300">
              {i18n(`metric.${metric.toLowerCase()}.short`)}
            </Typography>
          )}
        </Stack>
        <Typography variant="text1xl400" color="dsNeutral.v300">
          {metricName}
        </Typography>
      </Stack>

      <Box position="relative" width="100%">
        <LinearProgress
          variant="determinate"
          value={Math.min(metricProgress, 100)}
          aria-label={metricName}
          sx={{
            height: 14,
            borderRadius: 8,
            border: `${theme.spacing(0.4)} solid ${theme.palette.dsBase.v200}08`,
            backgroundColor: `${theme.palette.dsBase.v200}08`,
            backgroundClip: "padding-box",
            "& .MuiLinearProgress-bar": {
              background: progressGradient,
              borderRadius: 8,
              transition: "transform 700ms ease-out",
            },
          }}
        />
        {targetProgress !== undefined && target !== undefined && target > 0 && (
          <Box
            position="absolute"
            left={`${Math.min(targetProgress, 100)}%`}
            bottom={-12}
            sx={{ transform: "translateX(-50%)", pointerEvents: "none" }}
          >
            <svg width={10} height={10} viewBox="0 0 10 10">
              <polygon
                points="5,4.5 1,9 9,9"
                fill={theme.palette.dsNeutral.v300}
                stroke={theme.palette.dsNeutral.v300}
                strokeWidth="2"
                strokeLinejoin="round"
              />
            </svg>
          </Box>
        )}
      </Box>
    </Stack>
  );
};

export default BootcampMetricResult;
